import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import MUITable from "Components/UI/Tables/MUITable";
import useTableOptions from "Hooks/useTableOptions";

import { getAccountCharts, getShortInfoClients } from "API/swesum";
import {
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
} from "@mui/material";
import DateWithClear from "Components/UI/Form/DateWithClear";
import dayjs from "dayjs";
import styled from "@emotion/styled";
import { useBalances } from "Context/Balances";
import { useClients } from "Context/Clients";
import { usePopupNotificationContext } from "Context/NotificationPopup";

const createTableRows = (balances, accounts) => {
  const rows = balances?.map((i) => {
    const account = accounts.find(
      (a) => Number(a.Id) === Number(i.AccountChartId)
    );
    return [
      {
        AccountId: i.AccountChartId,
        AccountNumber: i?.AccountChartNumber,
        Date: i.Day,
      },
      i.Daybalance,
      dayjs(i.Day).format("YYYY-MM-DD"),
      { label: i.ClientNumber, id: i.ClientId },
    ];
  });

  return rows;
};

const StyledTable = styled(MUITable)(({ theme }) => ({
  border: "none",
  boxShadow: "none",
  paddingBottom: "16px",
  ".MuiTable-root": {
    ".MuiTableCell-root": {
      "&:nth-of-type(1)": {
        paddingTop: "0",
        paddingBottom: "0",
      },
    },
  },
}));

export default function BalancesTable() {
  const { isColDisplayed } = useTableOptions();
  const { fetchBalances, balances, loading, isInitialized } = useBalances();
  const [clients, setClients] = useState([]);
  const [rows, setRows] = useState([]);
  const [selectedAccountIds, setSelectedAccountIds] = useState([]);
  const [date, setDate] = useState(null);
  const [selectedClientId, setSelectedClientId] = useState(-1);
  const [accounts, setAccounts] = useState([]);
  const [filters, setFilters] = useState([]);

  const { openError } = usePopupNotificationContext();

  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    const asyncGetExtraInfo = async () => {
      const accountCharts = await getAccountCharts();
      setAccounts(accountCharts);
    };

    if (isInitialized) {
      asyncGetExtraInfo();
    }
  }, [isInitialized]);

  useEffect(() => {
    const rows = createTableRows(balances, accounts);
    setRows(rows);
  }, [balances, accounts]);

  useEffect(() => {
    const asyncFetchBalances = async (filters) => {
      const res = await fetchBalances(
        {
          Filters: filters,
          Order: {
            Column: "Day",
            Order: 0,
          },
          Page: 0,
          PageSize: rowsPerPage,
        },
        selectedClientId > -1 ? selectedClientId : null,
        true
      );
    };

    // FORCE NEW FETCH
    const filters = [];

    if (date) {
      filters.push({
        Column: "Day",
        Value: dayjs(date).format("YYYY-MM-DD"),
        Operation: 0,
        Or: false,
      });
    }

    if (selectedAccountIds.length > 0) {
      filters.push({
        Column: "AccountId",
        Value: selectedAccountIds,
        Operation: 7,
        Or: false,
      });
    }

    setFilters(filters);
    setPage(0);
    setRows([]);
    asyncFetchBalances(filters);
  }, [selectedAccountIds, date, selectedClientId]);

  useEffect(() => {
    if (isInitialized) {
      fetchBalances(
        {
          Filters: filters,
          Order: {
            Column: "Day",
            Order: 0,
          },
          Page: page,
          PageSize: rowsPerPage,
        },
        selectedClientId > -1 ? selectedClientId : null
      );
    }
  }, [rowsPerPage, page]);

  useEffect(() => {
    const asyncFetchClients = async () => {
      try {
        const res = await getShortInfoClients();
        setClients(res);
      } catch (_) {
        openError("Någonting gick fel vid hämtning av klienter");
      }
    };
    asyncFetchClients();
  }, []);

  const columns = [
    {
      name: "Kontonummer",
      options: {
        filter: false,
        customBodyRender: (value) => {
          const url = `/books?accountId=${value.AccountId}&date=${dayjs(
            value.Date
          ).format("YYYY-MM-DD")}&show=open&clientId=${selectedClientId}`;
          return <Link to={url}>{value.AccountNumber}</Link>;
        },
      },
    },
    {
      name: "Saldo",
      options: {
        filter: false,
        display: isColDisplayed("Saldo"),
      },
    },
    {
      name: "Datum",
      options: {
        filter: false,
        display: isColDisplayed("Datum"),
      },
    },
    {
      name: "Klient",
      options: {
        filter: false,
        display: isColDisplayed("Klient"),
        customBodyRender: (value) => {
          const url = `/clients/${value.id}`;
          return <Link to={url}>{value.label}</Link>;
        },
      },
    },
  ];

  const options = {
    selectableRows: "none",
    selectableRowsHideCheckboxes: true,
    textLabels: {
      body: {
        noMatch: loading ? <CircularProgress /> : "",
      },
    },
    filterType: "dropdown",
    responsive: "standard",
    downloadOptions: {
      separator: ";",
      filename: "all_balances.csv",
    },
    page: page,
    onChangePage: (currentPage) => setPage(currentPage),
    onChangeRowsPerPage: (numberOfRows) => setRowsPerPage(numberOfRows),
    pagination: false,
    rowsPerPage: rowsPerPage,
    count: totalCount,
  };

  return (
    <Paper elevation={5} sx={{ paddingX: "10px", marginTop: "30px" }}>
      <Grid item container spacing={2}>
        <Grid item xs={2}>
          <FormControl margin="dense" fullWidth sx={{ textAlign: "left" }}>
            <InputLabel>Kontonummer</InputLabel>
            <Select
              multiple
              name="AccountId"
              value={selectedAccountIds}
              onChange={(e) => {
                if (!!e.target.value) {
                  setSelectedAccountIds(e.target.value);
                }
              }}
              label="Kontonummer"
              size="small"
            >
              {accounts
                .sort((a, b) => a.AccountNumber.localeCompare(b.AccountNumber))
                .map((account) => (
                  <MenuItem key={account.Id} value={account.Id}>
                    {account.AccountNumber}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={2}>
          <DateWithClear
            label="Datum"
            value={date === "" ? "" : dayjs(date).format("YYYY-MM-DD")}
            onChange={(newVal) => setDate(newVal)}
          />
        </Grid>
        <Grid item xs={2}>
          <FormControl margin="dense" fullWidth sx={{ textAlign: "left" }}>
            <InputLabel>Klient</InputLabel>
            <Select
              name="Client"
              value={selectedClientId}
              onChange={(e) => {
                setSelectedClientId(
                  Number(e.target.value) === Number(selectedClientId)
                    ? null
                    : e.target.value
                );
              }}
              label="Klient"
              size="small"
            >
              <MenuItem key={-1} value={-1}>
                {"Ingen vald"}
              </MenuItem>
              {clients
                .sort((a, b) => a.Title.localeCompare(b.Title))
                .map((client) => (
                  <MenuItem key={client.Id} value={client.Id}>
                    {client.Title}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <StyledTable options={options} data={rows} columns={columns} />
    </Paper>
  );
}
